import { ActionContext } from "vuex";
import axiosAuth from "@/config/axios";
import { endPoints } from "@/config/api";
import { useNotification } from "@/composables/useNotification";
import {
  BirthdayPersonWithNotifications,
  BirthdayNotificationLog,
} from "../types/birthdays";
import { BirthdaysState } from "../types/state";
import { DefaultResponse, DefaultResponseItem } from "@/types/response";
import { IRootState } from "@/types/state";
import { formatDateToString } from "@/utils/date";

const { notifySuccess, notifyError } = useNotification();

function todayISO(): string {
  return formatDateToString(new Date());
}

function currentMonthISO(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

const state: () => BirthdaysState = () => ({
  list: [],
  selectedDate: todayISO(),
  selectedMonth: currentMonthISO(),
  viewMode: "month",
  typeFilter: "all",
  notificationFilter: "all",
  activeOnly: true,
  showFilterDialog: false,
  loading: false,
});

const mutations = {
  SET_LIST(state: BirthdaysState, payload: { list: BirthdayPersonWithNotifications[] }) {
    state.list = payload.list;
  },
  SET_SELECTED_DATE(state: BirthdaysState, payload: { date: string }) {
    state.selectedDate = payload.date;
  },
  SET_SELECTED_MONTH(state: BirthdaysState, payload: { month: string }) {
    state.selectedMonth = payload.month;
  },
  SET_VIEW_MODE(state: BirthdaysState, payload: { viewMode: BirthdaysState["viewMode"] }) {
    state.viewMode = payload.viewMode;
  },
  SET_LOADING(state: BirthdaysState, payload: { loading: boolean }) {
    state.loading = payload.loading;
  },
  SET_TYPE_FILTER(
    state: BirthdaysState,
    payload: { typeFilter: BirthdaysState["typeFilter"] }
  ) {
    state.typeFilter = payload.typeFilter;
  },
  SET_NOTIFICATION_FILTER(
    state: BirthdaysState,
    payload: { notificationFilter: BirthdaysState["notificationFilter"] }
  ) {
    state.notificationFilter = payload.notificationFilter;
  },
  SET_ACTIVE_ONLY(state: BirthdaysState, payload: { activeOnly: boolean }) {
    state.activeOnly = payload.activeOnly;
  },
  SHOW_FILTER_DIALOG(
    state: BirthdaysState,
    payload: { show: boolean }
  ) {
    state.showFilterDialog = payload.show;
  },
  ADD_NOTIFICATION(
    state: BirthdaysState,
    payload: { personKey: string; log: BirthdayNotificationLog }
  ) {
    const item = state.list.find(
      (p) => `${p.type}:${p.id}` === payload.personKey
    );
    if (item && !item.notifications.some((n) => n.id === payload.log.id)) {
      item.notifications = [...item.notifications, payload.log];
    }
  },
};

const actions = {
  setSelectedDate(
    { commit }: ActionContext<BirthdaysState, IRootState>,
    payload: { date: string }
  ) {
    commit("SET_SELECTED_DATE", { date: payload.date });
  },
  setSelectedMonth(
    { commit }: ActionContext<BirthdaysState, IRootState>,
    payload: { month: string }
  ) {
    commit("SET_SELECTED_MONTH", { month: payload.month });
  },
  setViewMode(
    { commit }: ActionContext<BirthdaysState, IRootState>,
    payload: { viewMode: BirthdaysState["viewMode"] }
  ) {
    commit("SET_VIEW_MODE", { viewMode: payload.viewMode });
  },
  setTypeFilter(
    { commit }: ActionContext<BirthdaysState, IRootState>,
    payload: { typeFilter: BirthdaysState["typeFilter"] }
  ) {
    commit("SET_TYPE_FILTER", { typeFilter: payload.typeFilter });
  },
  setNotificationFilter(
    { commit }: ActionContext<BirthdaysState, IRootState>,
    payload: { notificationFilter: BirthdaysState["notificationFilter"] }
  ) {
    commit("SET_NOTIFICATION_FILTER", { notificationFilter: payload.notificationFilter });
  },
  setActiveOnly(
    { commit }: ActionContext<BirthdaysState, IRootState>,
    payload: { activeOnly: boolean }
  ) {
    commit("SET_ACTIVE_ONLY", { activeOnly: payload.activeOnly });
  },
  openFilterDialog({ commit }: ActionContext<BirthdaysState, IRootState>) {
    commit("SHOW_FILTER_DIALOG", { show: true });
  },
  closeFilterDialog({ commit }: ActionContext<BirthdaysState, IRootState>) {
    commit("SHOW_FILTER_DIALOG", { show: false });
  },
  async fetchBirthdays(
    { commit, state }: ActionContext<BirthdaysState, IRootState>
  ) {
    commit("SET_LOADING", { loading: true });
    try {
      const params: Record<string, string | boolean> = {};
      if (state.viewMode === "month") {
        if (state.selectedMonth) params.month = state.selectedMonth;
      } else if (state.selectedDate) {
        params.date = state.selectedDate;
      }
      params.activeOnly = state.activeOnly;
      const response = await axiosAuth.get<
        DefaultResponse<BirthdayPersonWithNotifications>
      >(endPoints.birthdays, { params });
      const data = response.data?.data ?? [];
      commit("SET_LIST", { list: Array.isArray(data) ? data : [] });
    } catch (error) {
      notifyError("Erro ao buscar aniversariantes");
      commit("SET_LIST", { list: [] });
    } finally {
      commit("SET_LOADING", { loading: false });
    }
  },
  async createNotification(
    { commit, dispatch }: ActionContext<BirthdaysState, IRootState>,
    payload: {
      personType: "responsible" | "employee";
      personId: string;
      year: number;
    }
  ) {
    try {
      const response = await axiosAuth.post<
        DefaultResponseItem<BirthdayNotificationLog>
      >(`${endPoints.birthdays}/notifications`, {
        personType: payload.personType,
        personId: payload.personId,
        year: payload.year,
      });
      const log = response.data?.data;
      if (log) {
        const personKey = `${payload.personType}:${payload.personId}`;
        commit("ADD_NOTIFICATION", { personKey, log });
        notifySuccess("Notificação registrada para o ano " + payload.year);
      }
    } catch (error: any) {
      notifyError(
        error?.response?.data?.error || "Erro ao registrar notificação"
      );
    }
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};